// useEventDrag — pointer-based drag-to-reschedule controller.
//
// Why pointer events instead of HTML5 drag-and-drop: in the calendar's
// week/day views, timed events are `position: absolute` overlaying the
// hour-cell grid. HTML5 dragover events bubble up from the source
// element only along its own DOM-ancestor chain, so dropping inside
// the SAME column (where the dragged event sits) is fragile — the
// drop target never sees the event reliably. Pointer events sidestep
// this entirely: we just track pointermove and use elementFromPoint
// to identify the drop zone at release time.
//
// Drop zones declare themselves with one of two data attributes:
//   data-drop-hour-col="<YYYY-MM-DD>"  — hour grid for that date;
//                                         the hook reads pointerY
//                                         offset to compute hour
//   data-drop-day="<YYYY-MM-DD>"       — day target (whole-day move)
// Each view declares the zones via plain HTML attrs; this hook
// doesn't care about layout.

import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type { CalendarEvent } from "./rpc";

export interface EventDragController {
  /** Wire to the event element's `onPointerDown`. */
  onPointerDown: (evt: CalendarEvent) => (e: PointerEvent) => void;
  /** True while a drag is in flight — views can use this to dim
   *  hover highlights or whatever. */
  active: boolean;
  /** Event id currently being dragged, for visual styling (e.g. faded
   *  source row). */
  draggingId: string | null;
}

export interface EventDragHandlers {
  /** Resolved when the user releases over an hour drop zone. The hook
   *  passes the date attribute parsed back to a Date plus the snapped
   *  hour (decimal, half-hour resolution). */
  onDropAtHour: (evt: CalendarEvent, day: Date, hour: number) => void;
  /** Resolved when release lands on a day-only drop zone (all-day
   *  shifts, month-cell moves). */
  onDropAtDay: (evt: CalendarEvent, day: Date) => void;
}

interface HourColInfo {
  startHour: number;
  rowH: number;
}

const HOUR_DATA_PREFIX = "drop-hour-col";
const DAY_DATA_KEY = "drop-day";
// Movement threshold (px) before we treat a pointerdown as a drag.
// Below this, the move is just a click and selecting the event still
// works. The threshold also dodges the iOS Safari "tap" sensitivity.
const DRAG_THRESHOLD = 4;

export function useEventDrag(handlers: EventDragHandlers): EventDragController {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const [draggingId, setDraggingId] = useState<string | null>(null);

  // Live drag state — refs because pointermove/up listeners fire
  // dozens of times per second and we don't want a re-render on each.
  const evtRef = useRef<CalendarEvent | null>(null);
  const startXY = useRef<{ x: number; y: number } | null>(null);
  const armed = useRef(false);

  const onPointerDown = useCallback((evt: CalendarEvent) => (e: PointerEvent) => {
    // Only react to primary-button presses (left mouse / touch / pen).
    if (e.button !== 0 && e.pointerType === "mouse") return;
    evtRef.current = evt;
    startXY.current = { x: e.clientX, y: e.clientY };
    armed.current = true;
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!armed.current || !evtRef.current || !startXY.current) return;
      // Wait for the user to move past the threshold before we
      // promote this to a drag — otherwise a slow click would steal
      // the click from EventDetail.
      if (!draggingId) {
        const dx = e.clientX - startXY.current.x;
        const dy = e.clientY - startXY.current.y;
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        setDraggingId(evtRef.current.id);
      }
      // No live preview movement in this pass — feel-of-drop is
      // enough. A live ghost is a future polish item.
      e.preventDefault();
    };

    const cleanupDrag = () => {
      armed.current = false;
      evtRef.current = null;
      startXY.current = null;
      setDraggingId(null);
    };

    const onUp = (e: PointerEvent) => {
      if (!armed.current || !evtRef.current) { cleanupDrag(); return; }
      // No drag actually started — let the click flow through.
      if (!draggingId) { cleanupDrag(); return; }

      // Eat the synthetic click that the browser fires after pointerup
      // when down + up landed on related elements (common case: user
      // drops near where they started, and the click would re-open the
      // event detail). One-shot capture listener; setTimeout cleans up
      // if no click materializes (drop on non-clickable target).
      const suppressClick = (ev: MouseEvent) => {
        ev.stopPropagation();
        ev.preventDefault();
        window.removeEventListener("click", suppressClick, true);
      };
      window.addEventListener("click", suppressClick, true);
      window.setTimeout(() => window.removeEventListener("click", suppressClick, true), 50);

      const evt = evtRef.current;
      // Find the deepest element at the release point that carries
      // either data-drop-day or data-drop-hour-col. elementFromPoint
      // returns the topmost interactive element; we walk up to find
      // a drop-zone declaration.
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const zone = el?.closest<HTMLElement>(`[data-${HOUR_DATA_PREFIX}], [data-${DAY_DATA_KEY}]`);
      if (zone) {
        const hourCol = zone.getAttribute(`data-${HOUR_DATA_PREFIX}`);
        const dayKey = zone.getAttribute(`data-${DAY_DATA_KEY}`);
        if (hourCol && !evt.allDay) {
          // The view stamps startHour + rowH on data attrs so we can
          // back-compute the dropped hour from cursor Y.
          const info = readHourColInfo(zone);
          const rect = zone.getBoundingClientRect();
          const y = e.clientY - rect.top;
          const raw = info.startHour + y / info.rowH;
          // Snap to nearest half-hour, clamp to the column's range.
          const snapped = Math.max(info.startHour, Math.min(info.startHour + 24, Math.floor(raw * 2) / 2));
          handlersRef.current.onDropAtHour(evt, parseDateAttr(hourCol), snapped);
        } else if (dayKey) {
          handlersRef.current.onDropAtDay(evt, parseDateAttr(dayKey));
        }
      }
      cleanupDrag();
    };

    const onCancel = () => cleanupDrag();

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [draggingId]);

  return { onPointerDown, active: draggingId !== null, draggingId };
}

function readHourColInfo(el: HTMLElement): HourColInfo {
  const startHour = Number(el.getAttribute("data-drop-start-hour") || "0");
  const rowH = Number(el.getAttribute("data-drop-row-h") || "44");
  return { startHour, rowH };
}

function parseDateAttr(s: string): Date {
  // Pure date keys ("YYYY-MM-DD") get parsed as local-midnight to
  // avoid the UTC midnight shift that `new Date("YYYY-MM-DD")` does.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(s);
}
